import type {
  BillingPlan,
  BillingProviderAdapter,
  Entitlement,
  SubscriptionStatus,
} from "../types";

type MemberfulAdapterConfig = {
  apiBaseUrl: string;
  getAuthToken?: () => string | null;
};

async function memberfulFetch<T>(
  apiBaseUrl: string,
  path: string,
  init?: RequestInit,
  getAuthToken?: () => string | null
): Promise<T> {
  const token = getAuthToken?.();
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Memberful adapter request failed: ${response.status}`);
  }

  return (await response.json()) as T;
}

export function createMemberfulAdapter(config: MemberfulAdapterConfig): BillingProviderAdapter {
  const { apiBaseUrl, getAuthToken } = config;

  return {
    name: "memberful",
    capabilities: {
      inlinePortal: false,
      invoices: false,
      paymentMethods: false,
    },

    async getPlans() {
      return memberfulFetch<BillingPlan[]>(
        apiBaseUrl,
        "/billing/memberful/plans",
        undefined,
        getAuthToken
      );
    },

    async getCurrentEntitlement(userId: string) {
      return memberfulFetch<Entitlement>(
        apiBaseUrl,
        `/billing/memberful/entitlements?userId=${encodeURIComponent(userId)}`,
        undefined,
        getAuthToken
      );
    },

    async startCheckout(planId: string, returnUrl: string) {
      return memberfulFetch<{ checkoutUrl: string }>(
        apiBaseUrl,
        "/billing/memberful/checkout",
        {
          method: "POST",
          body: JSON.stringify({ planId, returnUrl }),
        },
        getAuthToken
      );
    },

    async openCustomerPortal(returnUrl: string) {
      return memberfulFetch<{ portalUrl: string }>(
        apiBaseUrl,
        "/billing/memberful/portal",
        {
          method: "POST",
          body: JSON.stringify({ returnUrl }),
        },
        getAuthToken
      );
    },

    async handleCallback(queryParams: URLSearchParams) {
      return memberfulFetch<{ ok: boolean; message?: string }>(
        apiBaseUrl,
        "/billing/memberful/callback",
        {
          method: "POST",
          body: JSON.stringify({ query: queryParams.toString() }),
        },
        getAuthToken
      );
    },

    async getSubscriptionStatus(userId: string) {
      return memberfulFetch<SubscriptionStatus>(
        apiBaseUrl,
        `/billing/memberful/subscription-status?userId=${encodeURIComponent(userId)}`,
        undefined,
        getAuthToken
      );
    },
  };
}
